/**
 * AppSwitcher — iOS-style multitasking view.
 *
 * Shown via swipe-up gesture from home screen.
 * Displays recently opened apps as cards that can be:
 *  - Tapped to re-enter the app
 *  - Swiped up to dismiss/clear from recents
 *  - "Clear All" to remove everything
 */

import { useState, useCallback, useRef } from 'react';
import { useAppSessionsStore, MediaIcon } from '@room/shared-ui';
import type { AppSession } from '@room/shared-ui';
import './AppSwitcher.css';

interface AppSwitcherProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenApp: (route: string) => void;
}

function isImageIcon(icon: string): boolean {
  return (
    icon.startsWith('http://') ||
    icon.startsWith('https://') ||
    icon.startsWith('data:image/') ||
    icon.startsWith('/api/')
  );
}

function CardIcon({ icon, id }: { icon: string; id: string }) {
  if (isImageIcon(icon)) {
    return <img src={icon} alt="" className="app-switcher__card-img" draggable={false} />;
  }
  return (
    <MediaIcon
      mediaId={id}
      fallback={icon}
      className="app-switcher__card-media-icon"
    />
  );
}

function AppCard({
  session,
  onOpen,
  onDismiss,
}: {
  session: AppSession;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  const [dismissed, setDismissed] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const tracking = useRef(false);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!e.isPrimary) return;
    startY.current = e.clientY;
    currentY.current = e.clientY;
    tracking.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!tracking.current || !e.isPrimary) return;
    currentY.current = e.clientY;
    const deltaY = currentY.current - startY.current;
    // Only allow upward swipe
    if (deltaY < 0 && cardRef.current) {
      cardRef.current.style.transform = `translateY(${deltaY}px)`;
      cardRef.current.style.opacity = `${Math.max(0, 1 + deltaY / 200)}`;
    }
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!tracking.current || !e.isPrimary) return;
      tracking.current = false;

      const deltaY = currentY.current - startY.current;
      if (deltaY < -80) {
        // Dismiss with animation
        setDismissed(true);
        setTimeout(onDismiss, 200);
      } else if (Math.abs(deltaY) < 8) {
        // Tap — open app
        onOpen();
      } else {
        // Reset position
        if (cardRef.current) {
          cardRef.current.style.transform = '';
          cardRef.current.style.opacity = '';
        }
      }
    },
    [onOpen, onDismiss],
  );

  return (
    <div
      ref={cardRef}
      className={`app-switcher__card ${dismissed ? 'app-switcher__card--dismissed' : ''}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      role="button"
      tabIndex={0}
    >
      <div className="app-switcher__card-header">
        <div className="app-switcher__card-icon">
          <CardIcon icon={session.icon} id={session.appId} />
        </div>
        <span className="app-switcher__card-name">{session.label}</span>
      </div>
      <div className="app-switcher__card-preview">
        <div className="app-switcher__card-placeholder">
          <span className="app-switcher__card-placeholder-icon">
            {isImageIcon(session.icon) ? '📱' : session.icon}
          </span>
        </div>
      </div>
    </div>
  );
}

export function AppSwitcher({ isOpen, onClose, onOpenApp }: AppSwitcherProps) {
  const { sessions, removeSession, clearAll } = useAppSessionsStore();

  const handleOpen = useCallback(
    (session: AppSession) => {
      onClose();
      onOpenApp(session.route);
    },
    [onClose, onOpenApp],
  );

  const handleClearAll = useCallback(() => {
    clearAll();
  }, [clearAll]);

  if (!isOpen) return null;

  return (
    <div className="app-switcher" onClick={onClose}>
      <div className="app-switcher__container" onClick={(e) => e.stopPropagation()}>
        <div className="app-switcher__header">
          <h2 className="app-switcher__title">Recent Apps</h2>
          {sessions.length > 0 && (
            <button className="app-switcher__clear-all" onClick={handleClearAll}>
              Clear All
            </button>
          )}
        </div>

        {sessions.length === 0 ? (
          <div className="app-switcher__empty">
            <div className="app-switcher__empty-icon">📱</div>
            <p>No recent apps</p>
          </div>
        ) : (
          <div className="app-switcher__cards">
            {sessions.map((session) => (
              <AppCard
                key={session.appId}
                session={session}
                onOpen={() => handleOpen(session)}
                onDismiss={() => removeSession(session.appId)}
              />
            ))}
          </div>
        )}

        <button className="app-switcher__close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
